import { Category } from '@mui/icons-material'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import { getImageUrl } from '../../utilities/Storage'
import {
  sqsMessage,
  WebSocketContext,
  WebSocketContextInterface,
} from '../WebSocket/WebSocketProvider'
import {
  MessageData,
  MessagesContextType,
  MessageThreadData,
  MessageUser,
  SendMessageData,
  ThreadsKey,
  ThreadsObj,
} from './types'

export const MessagesContext = createContext<MessagesContextType | null>(null)

export const MessagesProvider = ({ children }: { children?: ReactNode }) => {
  const { fetch } = useAxios()

  //websocket
  const { isConnected, sqsMessages } = useContext(
    WebSocketContext
  ) as WebSocketContextInterface

  const [isNewMessage, setIsNewMessage] = useState(false)

  const [navigatingDirectlyToThread, setNavigatingDirectlyToThread] =
    useState(false)

  const [threads, setThreads] = useState<ThreadsObj>({
    myPatients: [],
    providers: [],
    otherPatients: [],
    pastPatients: [],
  })

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)

  // attachments
  const [attachmentKeys, setAttachmentKeys] = useState<
    { id: number; key: string }[]
  >([])

  useEffect(() => {
    if (isConnected) {
      getProviderMessageThreads()
    }
  }, [isConnected])

  useEffect(() => {
    if (sqsMessages.length > 0 && threads.myPatients.length) {
      updateThreadsFromWebSocket(sqsMessages)
    }
  }, [sqsMessages])

  const addMessageToThread = async (newMessage: sqsMessage) => {
    const threadData = getCategoryThreadAndIndex(newMessage.threadId)

    // If indexInCategory === -1, the thread was not found and we need
    // to get all threads again to get this new thread.
    // When we get all threads, it will include this new message
    if (threadData.indexInCategory === -1) {
      await getProviderMessageThreads()
      return
    }

    const isMessageInThread = checkIfSQSMessageIsInThread(
      newMessage,
      threadData.thread.messages
    )

    if (isMessageInThread) {
      return
    }

    const isLatestMessage = checkIfMostRecentMessageInThread(
      newMessage,
      threadData.thread.messages
    )

    const updatedThread = {
      ...threadData.thread,
      unread: isLatestMessage,
      messages: [
        {
          messageId: newMessage.messageId,
          messageBody: newMessage.messageBody,
          fromUser: {
            id: newMessage.fromUser.id,
            name: newMessage.fromUser.name,
            role: newMessage.fromUser.role,
            photoUrl: newMessage.fromUser.photoUrl,
            isActive: newMessage.fromUser.isActive,
            isPatient: newMessage.fromUser.isPatient,
          } as unknown as MessageUser,
          toUsers: newMessage.toUsers?.map((u) => u),
          receivedDateTime: newMessage.receivedDateTime,
          urgent: false,
          attachments: newMessage.attachments ? newMessage.attachments : [],
          isFirstMessageInThread: false,
        },
        ...threadData.thread.messages,
      ],
    }

    const updatedThreads = { ...threads }

    updatedThreads[threadData.categoryName][threadData.indexInCategory] =
      updatedThread

    setThreads(updatedThreads)
  }

  const updateThreads = (updatedThreads: ThreadsObj) => {
    Object.values(updatedThreads).forEach((category: MessageThreadData[]) => {
      category.forEach((thread) =>
        updateThread(thread, 'getProviderMessageThreads')
      )
    })
  }

  const updateThread = (newThread: MessageThreadData, updatingFrom: string) => {
    const currentThreadData = getCategoryThreadAndIndex(
      newThread.messageThreadId
    )
    const updatedThreads = { ...threads }

    const mergedThread = mergeThreadDataWithExistingThread(
      currentThreadData.thread,
      newThread,
      updatingFrom
    )

    updatedThreads[currentThreadData.categoryName][
      currentThreadData.indexInCategory
    ] = mergedThread

    setThreads(updatedThreads)
  }

  const updateThreadsFromWebSocket = (newMessages: sqsMessage[]) => {
    newMessages.forEach((message) => addMessageToThread(message))
  }

  const mergeThreadDataWithExistingThread = (
    currentThread: MessageThreadData,
    newThread: MessageThreadData,
    updatingFrom: string
  ) => {
    const mergedThreads = {
      ...currentThread,
      unread:
        updatingFrom === 'getProviderMessageThreads' ? newThread.unread : false,
      displayUser:
        updatingFrom === 'getProviderMessageThreads'
          ? currentThread.displayUser
          : newThread.displayUser,
      displayThreadUserNames:
        updatingFrom === 'getProviderMessageThreads'
          ? currentThread.displayThreadUserNames
          : newThread.displayThreadUserNames,
      displayUserCounts:
        updatingFrom === 'getProviderMessageThreads'
          ? currentThread.displayUserCount
          : newThread.displayUserCount,
      otherUsers:
        updatingFrom === 'getProviderMessageThreads'
          ? currentThread.otherUsers
          : newThread.otherUsers,
      messages: deduplicateMessagesAndSortByDate(
        currentThread.messages,
        newThread.messages
      ),
    }
    return mergedThreads
  }

  const getProviderMessageThreads = async () => {
    const { data, error } = await fetch({
      path: `Message/GetProviderMessageThreads`,
    })
    if (data) {
      const isThreadsEmpty = checkAllThreadsEmpty()
      if (isThreadsEmpty) {
        setThreads(data)
      } else {
        updateThreads(data)
      }

      return data
    } else if (error) {
      throw new Error(`Error in getProviderMessageThreads.`)
    }
  }

  const getProviderPastPatientMessageThreads = async () => {
    const { data, error } = await fetch({
      path: `Message/GetProviderPastPatientMessageThreads`,
    })
    if (data) {
      data.forEach(async (d: MessageThreadData) => {
        if (d.displayUser.photoUrl) {
          d.displayUser.photoUrl = await getImageUrl(d.displayUser.photoUrl)
        }

        d.messages.forEach(async (m: MessageData) => {
          if (m.fromUser.photoUrl) {
            m.fromUser.photoUrl = await getImageUrl(m.fromUser.photoUrl)
          }
          m.toUsers.forEach(async (t: MessageUser) => {
            if (t.photoUrl) {
              t.photoUrl = await getImageUrl(t.photoUrl)
            }
          })
        })
      })
      const threadsWithPastPatientsAdded = { ...threads, pastPatients: data }
      setThreads(threadsWithPastPatientsAdded)

      return data
    } else if (error) {
      throw new Error(`Error in getProviderMessageThreads.`)
    }
  }

  // get all the messages in one thread
  const getProviderMessagesInThread = async (
    threadId: number,
    messageId: number
  ) => {
    const isThreadsEmpty = checkAllThreadsEmpty()

    if (isThreadsEmpty) {
      await getProviderMessageThreads()
    }

    const { data, error } = await fetch({
      path: `Message/GetProviderMessagesInThread?messageThreadId=${threadId}&messageId=${messageId}`,
    })
    if (data) {
      updateThread(data, 'getProviderMessagesInThread')
      return { ...data }
    } else if (error) {
      throw new Error(`Error in getProviderMessagesInThread.`)
    }
  }

  const checkIfThreadExists = async (patientId: number) => {
    const { data, error } = await fetch({
      path: `Message/CheckIfThreadExistsProvider?patientId=${patientId}`,
      methodType: 'GET',
    })

    return data
  }

  const sendMessage = async (sendMessageData: SendMessageData) => {
    const { error } = await fetch({
      path: `Message/SendProviderMessage`,
      methodType: 'POST',
      body: sendMessageData,
    })

    if (!isConnected && sendMessageData.messageThreadId !== undefined) {
      await getProviderMessagesInThread(sendMessageData.messageThreadId, 0)
    }

    if (!isConnected && sendMessageData.messageThreadId === undefined) {
      await getProviderMessageThreads()
    }

    if (error) {
      throw new Error(`Error in sendMessage.`)
    }
  }

  const getCurrentThread = () => {
    const threadsCopy = { ...threads }
    let currentThread = undefined
    Object.keys(threadsCopy).forEach((category) => {
      const categoryThreads = threadsCopy[category as keyof ThreadsObj]
      const thread = categoryThreads.find(
        (thread) => thread.messageThreadId === selectedThreadId
      )
      if (thread) {
        currentThread = thread
        return
      }
      return
    })
    return currentThread
  }

  const checkIfSQSMessageIsInThread = (
    newMessage: sqsMessage,
    messages: MessageData[]
  ) => {
    return messages.some(
      (message: MessageData) => message.messageId === newMessage.messageId
    )
  }

  const deduplicateMessagesAndSortByDate = (
    existingMessages: MessageData[],
    newMessages: MessageData[]
  ) => {
    const existingIds = new Set(existingMessages.map((msg) => msg.messageId))
    const uniqueNewMessages = newMessages.filter(
      (msg) => !existingIds.has(msg.messageId)
    )
    return [...existingMessages, ...uniqueNewMessages].sort(
      (a, b) =>
        new Date(b.receivedDateTime).getTime() -
        new Date(a.receivedDateTime).getTime()
    )
  }

  const checkIfMostRecentMessageInThread = (
    newMessage: sqsMessage,
    messages: MessageData[]
  ) => {
    const isMsgMostRecentMessage = messages.every(
      (message) =>
        new Date(message.receivedDateTime) <
        new Date(newMessage.receivedDateTime)
    )
    return isMsgMostRecentMessage
  }

  const getCategoryThreadAndIndex = (threadId: number) => {
    for (const [categoryName, category] of Object.entries(threads)) {
      const indexInCategory = category.findIndex(
        (thread: MessageThreadData) => thread.messageThreadId === threadId
      )
      if (indexInCategory !== -1) {
        const thread = category.find(
          (threadInCategory: MessageThreadData) =>
            threadInCategory.messageThreadId === threadId
        )
        return {
          categoryName: categoryName as ThreadsKey,
          thread,
          indexInCategory,
        }
      }
    }
    return { categoryName: '' as ThreadsKey, thread: {}, indexInCategory: -1 }
  }

  const checkAllThreadsEmpty = () => {
    const allEmpty = Object.values(threads).every(
      (threadCategory) => threadCategory.length === 0
    )
    return allEmpty
  }

  const currentThread = getCurrentThread()

  return (
    <MessagesContext.Provider
      value={{
        threads,
        currentThread,
        getProviderMessageThreads,
        selectedThreadId,
        setSelectedThreadId,
        getProviderMessagesInThread,
        getProviderPastPatientMessageThreads,
        checkIfThreadExists,
        sendMessage,
        attachmentKeys,
        setAttachmentKeys,
        isNewMessage,
        setIsNewMessage,
        navigatingDirectlyToThread,
        setNavigatingDirectlyToThread,
      }}
    >
      {children}
    </MessagesContext.Provider>
  )
}

export const useMessages = () =>
  useContext(MessagesContext) as MessagesContextType
